import { z } from 'zod';
import { NodeUpdateSchema } from './browser-tools.js';
import { BrowserRuntimeSchema, IdSchema, LocaleSchema, SupportSessionStatusSchema, TabInfoSchema, TimezoneSchema, UserRoleSchema, WindowSizeSchema, } from './common.js';
import { StreamModeSchema } from './modes.js';
// REST DTOs shared by server routes and the web client.
export const LoginBodySchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(256),
});
export const UserDtoSchema = z.object({
    id: IdSchema,
    email: z.string(),
    name: z.string(),
    role: UserRoleSchema,
    createdAt: z.string(),
});
export const CreateUserInviteBodySchema = z.object({
    email: z.string().email().max(254).optional(),
    role: UserRoleSchema.default('member'),
});
export const AcceptUserInviteBodySchema = z.object({
    token: z.string().min(16).max(128),
    name: z.string().min(1).max(64),
    email: z.string().email().max(254),
    password: z.string().min(8).max(256),
});
/**
 * Node registration. Two shapes:
 * - credentials: Node GUI / dev CLI exchanges operator email+password for a long-lived node token
 * - license: Node presents the vos-tools `license.jwt`; the server verifies it and registers the
 *   machine. The node socket accepts that license as its bearer credential.
 */
export const NodeRegisterCredentialsBodySchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(256),
    nodeName: z.string().min(1).max(64),
    hostname: z.string().max(128).optional(),
    os: z.string().max(64).optional(),
});
export const NodeRegisterLicenseBodySchema = z.object({
    licenseToken: z.string().min(16).max(8192),
    nodeName: z.string().min(1).max(64).optional(),
    hostname: z.string().max(128).optional(),
    os: z.string().max(64).optional(),
});
export const NodeRegisterBodySchema = z.union([
    NodeRegisterCredentialsBodySchema,
    NodeRegisterLicenseBodySchema,
]);
export const NodeDtoSchema = z.object({
    remoteUpdate: z.boolean().optional(),
    update: NodeUpdateSchema.optional(),
    id: IdSchema,
    name: z.string(),
    online: z.boolean(),
    lastSeenAt: z.string().nullable(),
    nodeVersion: z.string().nullable(),
    os: z.string().nullable(),
    hostname: z.string().nullable(),
    chromeAvailable: z.boolean().nullable(),
    modes: z.array(StreamModeSchema),
    headless: z.boolean().nullable(),
    openProfileCount: z.number().int(),
    createdAt: z.string(),
});
export const ProxyInputSchema = z.object({
    server: z.string().min(1).max(512),
    username: z.string().max(256).optional(),
    /** omitted or empty on update = keep existing */
    password: z.string().max(256).optional(),
    bypass: z.string().max(1024).optional(),
});
/** null uses the server default, 0 keeps the browser open indefinitely. */
export const BrowserIdleMinutesSchema = z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .nullable();
export const CreateBrowserProfileBodySchema = z.object({
    nodeId: IdSchema,
    name: z.string().min(1).max(120),
    customerLabel: z.string().max(120).default(''),
    initialUrl: z.string().max(2048).optional(),
    defaultMode: StreamModeSchema.default('screencast'),
    size: WindowSizeSchema.optional(),
    proxy: ProxyInputSchema.nullable().optional(),
    /** override node headless setting for this profile */
    headless: z.boolean().nullable().optional(),
    /** region the browser presents; match the proxy's exit region. Empty = node's system settings */
    locale: LocaleSchema.nullable().optional(),
    timezoneId: TimezoneSchema.nullable().optional(),
    idleMinutes: BrowserIdleMinutesSchema.optional(),
    /** open Chrome immediately after creating */
    openNow: z.boolean().default(true),
    /** also create a customer support session right away */
    createSupportSession: z.boolean().default(true),
});
export const UpdateBrowserProfileBodySchema = z.object({
    name: z.string().min(1).max(120).optional(),
    customerLabel: z.string().max(120).optional(),
    defaultMode: StreamModeSchema.optional(),
    size: WindowSizeSchema.optional(),
    proxy: ProxyInputSchema.nullable().optional(),
    headless: z.boolean().nullable().optional(),
    /** null clears the override */
    locale: LocaleSchema.nullable().optional(),
    timezoneId: TimezoneSchema.nullable().optional(),
    idleMinutes: BrowserIdleMinutesSchema.optional(),
});
export const SupportSessionSummarySchema = z.object({
    id: IdSchema,
    status: SupportSessionStatusSchema,
    expiresAt: z.string(),
    revokedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const BrowserProfileDtoSchema = z.object({
    id: IdSchema,
    nodeId: IdSchema,
    nodeName: z.string(),
    nodeOnline: z.boolean(),
    name: z.string(),
    customerLabel: z.string(),
    runtime: BrowserRuntimeSchema,
    defaultMode: StreamModeSchema,
    size: WindowSizeSchema,
    proxy: z
        .object({
        server: z.string(),
        username: z.string().nullable(),
        hasPassword: z.boolean(),
        bypass: z.string().nullable(),
    })
        .nullable(),
    headless: z.boolean().nullable(),
    locale: z.string().nullable(),
    timezoneId: z.string().nullable(),
    /** per-profile override; null means use the server-wide default */
    idleMinutes: BrowserIdleMinutesSchema,
    effectiveIdleMinutes: z
        .number()
        .int()
        .min(0)
        .max(24 * 60),
    createdBy: IdSchema,
    createdByName: z.string(),
    participantCount: z.number().int(),
    controllerName: z.string().nullable(),
    activeTabId: IdSchema.nullable(),
    tabs: z.array(TabInfoSchema),
    latestSupportSession: SupportSessionSummarySchema.nullable(),
    lastOpenedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    archivedAt: z.string().nullable(),
});
export const CreateSupportSessionBodySchema = z.object({
    expiresInMinutes: z
        .number()
        .int()
        .min(5)
        .max(60 * 24 * 30)
        .default(60 * 24),
    maxUses: z.number().int().min(1).max(100).default(5),
});
export const UpdateSupportSessionBodySchema = z.object({
    status: SupportSessionStatusSchema,
});
export const SupportSessionDtoSchema = SupportSessionSummarySchema.extend({
    profileId: IdSchema,
    token: z.string(),
    code: z.string(),
    url: z.string(),
    maxUses: z.number().int(),
    useCount: z.number().int(),
});
export const JoinBodySchema = z
    .object({
    token: z.string().min(8).max(128).optional(),
    code: z.string().min(4).max(16).optional(),
    label: z.string().min(1).max(64),
})
    .refine((v) => Boolean(v.token || v.code), { message: 'token or code required' });
export const JoinResponseSchema = z.object({
    profileId: IdSchema,
    profileName: z.string(),
    supportSessionId: IdSchema,
    participantLabel: z.string(),
});
export const AuditEventDtoSchema = z.object({
    /** Opaque, monotonically increasing per profile; pass back as `before` for older events */
    id: z.string(),
    profileId: IdSchema.nullable(),
    tabId: IdSchema.nullable(),
    actorType: z.enum(['user', 'customer', 'node', 'system']),
    actorId: z.string().nullable(),
    actorName: z.string().nullable(),
    type: z.string(),
    payload: z.record(z.string(), z.unknown()).nullable(),
    at: z.string(),
});
export const ApiErrorSchema = z.object({
    error: z.object({ code: z.string(), message: z.string() }),
});
//# sourceMappingURL=rest.js.map